
import { defineStore } from 'pinia';
import { ref, get, set, onValue } from 'firebase/database';
import { database } from '../firebase';

export const useRolesStore = defineStore('roles', {
  state: () => ({
    userRole: null,
    availableRoles: {
      'listener': {
        name: 'Listener',
        permissions: ['play', 'browse']
      },
      'listener_account': {
        name: 'Listener/Account', 
        permissions: ['play', 'browse', 'playlist', 'share', 'comment']
      },
      'creator': {
        name: 'Creator',
        permissions: ['play', 'browse', 'playlist', 'share', 'comment', 'upload', 'analytics', 'google_cloud', 'spotify']
      },
      'enterprise': {
        name: 'Enterprise',
        permissions: ['play', 'browse', 'playlist', 'share', 'comment', 'upload', 'analytics', 'google_cloud', 'spotify', 'multi_user', 'detailed_analytics']
      },
      'admin': {
        name: 'Admin',
        permissions: ['*'] // All permissions
      }
    },
    loading: false,
    error: null
  }),

  actions: {
    async initializeUserRole(userId) {
      if (!userId) {
        console.log('No userId provided, setting role to null');
        this.userRole = null;
        return;
      }

      try {
        this.loading = true;
        console.log('Initializing user role for:', userId);
        const userRoleRef = ref(database, `user_roles/${userId}`);
        const snapshot = await get(userRoleRef);
        
        if (snapshot.exists()) {
          const roleData = snapshot.val();
          this.userRole = roleData.role;
          console.log('Existing role found:', roleData.role);
        } else {
          // Default role for new users
          console.log('No existing role, setting default: listener_account');
          this.userRole = 'listener_account';
          await this.setUserRole(userId, 'listener_account');
        }
      } catch (error) {
        console.error('Error loading user role:', error);
        this.error = error.message;
        this.userRole = 'listener_account'; // Fallback
      } finally {
        this.loading = false;
      }
    },

    async setUserRole(userId, role) {
      if (!userId || !this.availableRoles[role]) {
        console.log('Invalid userId or role:', { userId, role });
        return;
      }

      try {
        console.log(`Setting user role: ${userId} -> ${role}`);
        const userRoleRef = ref(database, `user_roles/${userId}`);
        await set(userRoleRef, {
          role: role,
          assignedAt: new Date().toISOString()
        });
        this.userRole = role;
        console.log(`User role set successfully: ${role}`);
      } catch (error) {
        console.error('Error setting user role:', error);
        this.error = error.message;
      }
    },

    hasPermission(permission) {
      if (!this.userRole) return false;
      
      const roleConfig = this.availableRoles[this.userRole];
      if (!roleConfig) return false;
      
      // Admin has all permissions
      if (roleConfig.permissions.includes('*')) return true;
      
      return roleConfig.permissions.includes(permission);
    },

    canUpload() {
      return this.hasPermission('upload');
    },

    canManageUsers() {
      return this.hasPermission('*'); // Only admin for now
    }
  }
});
